import React, { useState } from 'react';
import {
  Search,
  X,
  Calendar,
  Trash2,
} from 'lucide-react';

interface Holiday {
  id: string;
  name: string;
  date: string;
  type: 'religious' | 'cultural' | 'national' | 'other';
  description?: string;
}

interface CountryHolidays {
  country: string;
  holidays: Holiday[];
}

const mockHolidays: CountryHolidays[] = [
  {
    country: 'Israel',
    holidays: [
      { id: '1', name: 'Rosh Hashanah', date: 'Sep 15-16', type: 'religious', description: 'Jewish New Year' },
      { id: '2', name: 'Yom Kippur', date: 'Sep 24', type: 'religious', description: 'Day of Atonement' },
      { id: '3', name: 'Sukkot', date: 'Sep 29 - Oct 6', type: 'religious', description: 'Feast of Tabernacles' },
      { id: '4', name: 'Independence Day', date: 'May 14', type: 'national', description: 'Israeli Independence Day' },
    ],
  },
];

const typeColors: Record<string, string> = {
  religious: 'bg-purple-100 text-purple-700',
  cultural: 'bg-blue-100 text-blue-700',
  national: 'bg-green-100 text-green-700',
  other: 'bg-gray-100 text-gray-700',
};

export function DatesHolidaysSection() {
  const [searchQuery, setSearchQuery] = useState('');
  const [countryHolidays, setCountryHolidays] = useState<CountryHolidays[]>(mockHolidays);
  const [showCountrySearch, setShowCountrySearch] = useState(false);

  const suggestedCountries = [
    'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 'France'
  ].filter((c) => c.toLowerCase().includes(searchQuery.toLowerCase()));

  const addCountry = (country: string) => {
    if (!countryHolidays.find((ch) => ch.country === country)) {
      // In real app, fetch holidays for the country
      setCountryHolidays([
        ...countryHolidays,
        {
          country,
          holidays: [
            { id: Date.now().toString(), name: 'New Year\'s Day', date: 'Jan 1', type: 'national' },
          ],
        },
      ]);
    }
    setSearchQuery('');
    setShowCountrySearch(false);
  };

  const removeCountry = (country: string) => {
    setCountryHolidays((prev) => prev.filter((ch) => ch.country !== country));
  };

  const removeHoliday = (country: string, holidayId: string) => {
    setCountryHolidays((prev) =>
      prev.map((ch) =>
        ch.country === country
          ? { ...ch, holidays: ch.holidays.filter((h) => h.id !== holidayId) }
          : ch
      )
    );
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-2">Add Holidays By Country or Holiday name</h2>
      <p className="text-sm text-gray-500 mb-4">
        Search for a country to automatically add all its official holidays, or search for specific holidays.
      </p>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setShowCountrySearch(true);
          }}
          onFocus={() => setShowCountrySearch(true)}
          placeholder="Search country or holiday..."
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
        />
        {showCountrySearch && searchQuery && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
            {suggestedCountries.length > 0 ? (
              suggestedCountries.map((country) => (
                <button
                  key={country}
                  onClick={() => addCountry(country)}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                >
                  {country}
                </button>
              ))
            ) : (
              <div className="px-4 py-2 text-sm text-gray-500">No results found</div>
            )}
          </div>
        )}
      </div>

      {/* Country Sections */}
      <div className="space-y-6">
        {countryHolidays.map((ch) => (
          <div key={ch.country} className="border border-gray-200 rounded-lg overflow-hidden">
            {/* Country Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
              <h3 className="font-medium text-gray-900">{ch.country}</h3>
              <button
                onClick={() => removeCountry(ch.country)}
                className="text-sm text-red-600 hover:text-red-700"
              >
                Remove Country
              </button>
            </div>

            {/* Holiday Cards */}
            <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {ch.holidays.map((holiday) => (
                <div
                  key={holiday.id}
                  className="relative p-3 border border-gray-200 rounded-lg hover:shadow-sm"
                >
                  <button
                    onClick={() => removeHoliday(ch.country, holiday.id)}
                    className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500"
                  >
                    <X className="w-3 h-3" />
                  </button>
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-teal-600" />
                    <span className="text-sm font-medium text-teal-600">{holiday.date}</span>
                  </div>
                  <h4 className="font-medium text-gray-900 text-sm mb-1">{holiday.name}</h4>
                  {holiday.description && (
                    <p className="text-xs text-gray-500 mb-2">{holiday.description}</p>
                  )}
                  <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${typeColors[holiday.type]}`}>
                    {holiday.type.charAt(0).toUpperCase() + holiday.type.slice(1)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {countryHolidays.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No holidays added yet</p>
          <p className="text-sm">Search for a country to add its holidays</p>
        </div>
      )}
    </div>
  );
}
